import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { InlineInputGroup, Select, Option } from 'barnyard/components';
import RuleForm from '../inputs/RuleForm';
import InlineSupplierSelect from '../inputs/InlineSupplierSelect';
import InlineManufacturerSelect from '../inputs/InlineManufacturerSelect';
import InlineRegionSelect from '../inputs/InlineRegionSelect';
import InlineItemSelect from '../inputs/InlineItemSelect';
import InlineCategorySelect from '../inputs/InlineCategorySelect';


const SecondarySupplierGuidance = (props) => {
  const {
    onSubmit: propSubmit,
    initialValues,
    form,
    type,
    isCorporate,
    ...other
  } = props;
  const initialState = {
    supplier_ids: initialValues.supplier_ids || [],
    manufacturer_ids: initialValues.manufacturer_ids || [],
    excluded_manufacturer_ids: initialValues.excluded_manufacturer_ids || [],
    item_ids: initialValues.item_ids ? _.join(initialValues.item_ids) : '',
    excluded_item_ids: initialValues.excluded_item_ids ? _.join(initialValues.excluded_item_ids) : '',
    regions: !_.isEmpty(initialValues.regions) ? initialValues.regions : [0],
    preference_level: initialValues.preference_level || 'default',
    category_ids: initialValues.category_ids || [],
    excluded_category_ids: initialValues.excluded_category_ids || []
  };
  const [supplierIds, setSupplierIds] = useState(initialState.supplier_ids);
  const [manufacturerIds, setManufacturerIds] = useState(initialState.manufacturer_ids);
  const [excludedManufacturerIds, setExcludedManufacturerIds] = useState(initialState.excluded_manufacturer_ids);
  const [itemIds, setItemIds] = useState(initialState.item_ids);
  const [excludedItemIds, setExcludedItemIds] = useState(initialState.excluded_item_ids);
  const [regions, setRegions] = useState(initialState.regions);
  const [preferenceLevel, setPreferenceLevel] = useState(initialState.preference_level);
  const [categoryIds, setCategoryIds] = useState(initialState.category_ids);
  const [excludedCategoryIds, setExcludedCategoryIds] = useState(initialState.excluded_category_ids);
  const [showValue, setShowValue] = useState(!_.isEmpty(supplierIds));
  useEffect(() => setShowValue(!_.isEmpty(supplierIds)), [supplierIds]);

  const onSubmit = (event) => {
    event.preventDefault();
    const value = {
      supplier_ids: supplierIds,
      manufacturer_ids: showValue ? manufacturerIds : [],
      excluded_manufacturer_ids: showValue ? excludedManufacturerIds : [],
      item_ids: showValue && itemIds ? _.map(_.split(itemIds, ','), _.toInteger) : [],
      excluded_item_ids: showValue && excludedItemIds ? _.map(_.split(excludedItemIds, ','), _.toInteger) : [],
      category_ids: showValue ? categoryIds : [],
      excluded_category_ids: showValue ? excludedCategoryIds : [],
      preference_level: preferenceLevel
    };
    propSubmit({
      kind: 'secondary_supplier_guidance',
      regions: _.filter(regions),
      value
    });
  };

  const onReset = () => {
    setSupplierIds(initialState.supplier_ids);
    setManufacturerIds(initialState.manufacturer_ids);
    setExcludedManufacturerIds(initialState.excluded_manufacturer_ids);
    setItemIds(initialState.item_ids);
    setExcludedItemIds(initialState.excluded_item_ids);
    setCategoryIds(initialState.category_ids);
    setExcludedCategoryIds(initialState.excluded_category_ids);
    setRegions(initialState.regions);
    setPreferenceLevel(initialState.preference_level);
  };

  const showUpdatedOrInitialValue = (value, initialValue) => (showValue ? value : initialValue);

  return (
    <RuleForm
      {...other}
      onSubmit={onSubmit}
      onReset={onReset}
      isSubmitting={form.is_submitting}
    >
      <InlineRegionSelect
        onChange={setRegions}
        value={regions}
      />
      <InlineSupplierSelect
        placeholder='All suppliers by default'
        mode='multiple'
        showSearch={true}
        value={supplierIds}
        onChange={setSupplierIds}
        include_all={false}
        include_any={false}
      />
      <InlineManufacturerSelect
        label='Manufacturers'
        value={manufacturerIds}
        placeholder='All manufacturers by default'
        showSearch={true}
        onChange={setManufacturerIds}
        disabled={!showValue}
      />
      <InlineManufacturerSelect
        label='Excluded Manufacturers'
        description='Manufacturers to be excluded'
        value={excludedManufacturerIds}
        placeholder='No manufacturers excluded by default'
        showSearch={true}
        onChange={setExcludedManufacturerIds}
        disabled={!showValue}
      />
      <InlineItemSelect
        placeholder='All items by default'
        label='Items'
        description='Items to be included. Please specify suppliers to include items.'
        value={showUpdatedOrInitialValue(itemIds, initialState.item_ids)}
        onChange={setItemIds}
        disabled={!showValue}
      />
      <InlineItemSelect
        placeholder='No items excluded by default'
        label='Excluded Items'
        description='Items to be excluded. Please specify suppliers to exclude items.'
        value={showUpdatedOrInitialValue(excludedItemIds, initialState.excluded_item_ids)}
        onChange={setExcludedItemIds}
        disabled={!showValue}
      />
      <InlineCategorySelect
        placeholder='All categories by default'
        label='Categories'
        description='Categories to be included. Please specify suppliers to include categories.'
        value={showUpdatedOrInitialValue(categoryIds, initialState.category_ids)}
        onChange={setCategoryIds}
        disabled={!showValue}
      />
      <InlineCategorySelect
        placeholder='No categories excluded by default'
        label='Excluded Categories'
        description='Categories to be excluded. Please specify suppliers to exclude categories.'
        value={showUpdatedOrInitialValue(excludedCategoryIds, initialState.excluded_category_ids)}
        onChange={setExcludedCategoryIds}
        disabled={!showValue}
      />
      <InlineInputGroup
        label='Preference Strength'
        description='How strongly these items are preferred'
        InputComponent={Select}
        onChange={setPreferenceLevel}
        defaultValue={false}
        value={preferenceLevel}
        useNative={true}
      >
        <Option value='default'>Default (recommended, light gray highlight)</Option>
        {isCorporate && <Option value='strong'>Strong (blue highlight)</Option>}
      </InlineInputGroup>
    </RuleForm>
  );
};

SecondarySupplierGuidance.defaultProps = {
  type: 'add',
  initialValues: {}
};

SecondarySupplierGuidance.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  form: PropTypes.object.isRequired,
  type: PropTypes.string,
  initialValues: PropTypes.object,
  corporate: PropTypes.object,
  isCorporate: PropTypes.bool,
};

export default SecondarySupplierGuidance;
